/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* editscherm.ts
* 16-12-2019 - Jelmer Jellema - Spin in het Web B.V.
*
* Abstracte parent van schermen met een dialoog voor één record. We regelen de code zoveel mogeijk algemeen.
* Krijgt een injector van de concrete classes
*
* Dit is de oude versie voor Datarecord objecten, geen modellen
*f
* Verwacht:
* <form #form="ngForm"> Als het hoofdform
*
*/

import {AfterViewInit, Directive, HostBinding, Input, OnInit, ViewChild} from '@angular/core';
import {Appscherm} from './appscherm';
import {Datarecord, Datarecords, Recordquery} from '../../services/api/api.service';
import {NgForm} from '@angular/forms';
import {cloneDeep} from 'lodash-es';
import {hash} from '../../app/types';

@Directive() //nodig want classes met ng-events of angular decorators moeten gedecoreerd zin
export abstract class Editscherm extends Appscherm implements OnInit, AfterViewInit {
  //zet de .editscherm class voor algemene styling

  @HostBinding('class.editscherm') readonly _hostclasseditscherm = true;
  @ViewChild('form', {static: false}) form: NgForm;
  @Input('id') editId: any = null; //het te editen id

  protected abstract readonly table: string; //moet overschreven worden: de hoofdtabel voor het record
  protected readonly defaultOptions: hash<any> = {}; //api.records opties buiten 'table' en 'single', dus connected, attributes etc. Maar even als een Any. Ingewikkelder zaken kunnen via de getter recordOptions gehaald worden

  protected readonly lookupQueries: hash<Recordquery> = {}; //informatie die ook opgehaald mooet worden. Key is de key in lookups en lookupHashes, value is een algemene recordquery. Wij zetten de table van het editscherm erin als lookup, tenzij lookup: false gegeven is (dan dus geen lookup).

  private action: 'save' | 'delete' = null; //wat zijn we aan het doen

  record: Datarecord = {}; //het model
  orig: Datarecord = {}; //de data zoals het laatst opgehaald
  kanVerwijderen: boolean = false; //wordt apart gezet
  readonly: boolean = false; //hebben wij besloten dat het readonly is?
  protected defaultRecord: Datarecord = {}; //overschrijfbaar, het defaultrecord bij een nieuwe
  lookups: hash<Datarecords> = {}; //lookupdata per lookupkey
  lookupHashes: hash<hash<Datarecord>> = {}; //lookupdata per lookupkey, op id
  shouldReload: boolean = false; //flag, gaat op true bij datawijziging, en op false bij reload

  protected constructor(loglevel: string = 'warn') {
    super(loglevel);
  }

  public get isNieuw(): boolean {
    return !this.editId;
  }

  async ngOnInit() {
    // test acl
    if (this.api.can(this.table, 'readonly')) {
      this.readonly = true;
      if (this.isNieuw) {
        //laat maar
        this.log.warn(`edit-nieuw maar readonly`);
        setTimeout(() => this.closeWindow());
        return;
      }
    } else if (!this.api.can(this.table, 'full')) {
      //geen toestemming
      this.log.warn(`Edit: geen toestemming`);
      setTimeout(() => this.closeWindow());
      return;
    }

    // this.readonly = true; //test

    //haal de data

    await this.haalData();

    //Zet observer op de datawijzigingen
    if (this.editId) {
      this.subscription(this.api.onRecordwijziging(this.table, this.editId).subscribe(wijziging => {
        if (wijziging.delete) {
          if (this.action !== 'delete') {
            //record is verwijderd, en niet door ons
            this.api.toast(`Een andere gebruiker heeft ${this.recordBeschrijving} verwijderd...`, 'warning');
            this.closeWindow();
            return;
          }
        } else if (this.action !== 'save') {
          //een ander slaat het op
          //this.api.toast(`Een andere gebruiker heeft ${this.recordBeschrijving} gewijzigd. Sluit of herlaad de gegevens.`, 'warning');
          this.shouldReload = true;
        }
      }));
    }
    this.subscription(this.api.onDatawijziging(Object.keys(this.lookupQueries)).subscribe(() => {
      //haal de lookups opnieuw op. Alleen de gewijzigde zijn niet meer in de cache
      this.haalLookups();
    }));

    void this.dataGezet(); //hook voor subclasses
  }

  ngAfterViewInit(): void {

    //nu is er het form
    //anders alles readonly
    if (this.readonly && this.form) {
      setTimeout(() => {
        this.form.form.disable();
      });
    }
  }

  private async haalData() {
    if (this.editId) {
      try {
        this.record = await this.api.cachedRecords(Object.assign({}, this.recordOptions, {
          table: this.table,
          single: this.editId
        }));
      } catch (e) {
        this.closeWindow();
        return;
      }
      //kanVerwijderen altijd apart, zonder caching
      this.kanVerwijderen = await this.api.canDelete(this.table, this.editId);
    } else {
      //nieuw: dus de defaults
      this.record = Object.assign({}, this.defaultRecord);
      this.kanVerwijderen = false; //want er is niets
    }
    this.orig = cloneDeep(this.record); //kopie van de data zoals opgehaald

    await this.haalLookups(); //wat niet gewijzigd is zit nog in de cache
    this.shouldReload = false;

    //klaar: hook
    this.afterHaaldata();
  }

  /**
   * Haal de lookups (opnieuw) op
   * @protected
   */
  protected async haalLookups() {
    for (let key of Object.keys(this.lookupQueries)) {
      let lookupQuery = cloneDeep(this.lookupQueries[key]);
      if (!('lookup' in lookupQuery)) {
        //onze table
        lookupQuery.lookup = this.table;
      } else if (!lookupQuery.lookup) {
        //expliciet false
        delete lookupQuery.lookup; //niet mee
      }

      try {
        this.lookups[key] = <Datarecords> await this.api.cachedRecords(lookupQuery);
        this.lookupHashes[key] = {};
        for (let r of this.lookups[key]) {
          this.lookupHashes[key][r.id] = r;
        }
      } catch (e) {
        this.log.debug(`Error in ophalen lookup ${key}`);
      }
    }
    //klaar: hook
    this.afterHaalLookups();
  }


  /////////////////// STANDAARD BUTTON HANDLERS ///////////////////////////////////
  public async reload() {
    //opnieuw laden
    if (this.form) //zou er moeten zijn
    {
      this.form.resetForm();
    }
    await this.haalData();
  }

  /**
   * Save het record
   */
  public async save(): Promise<void> {
    if (this.readonly || (!this.api.can(this.table, 'full'))) {
      return;
    }
    try {
      const savedata = this.savedata(); //hook voor omzetten data voor het saven
      this.log.debug(`Save`, this.record, savedata);
      this.action = 'save'; //bezig
      let res = await this.api.save({
        table: this.table,
        data: savedata
      });
      if (this.isNieuw) {
        this.record.id = res.saved; //id ook goed zetten, voor teruggeven resultaat
      }
      //gelukt
      this.form.form.markAsPristine();
      this.afterSave();
    } catch (e) {
      this.log.error(e);
    }
    this.action = null;
  }

  /**
   * Verwijder het record
   */

  public async delete(): Promise<void> {
    if (!this.api.can(this.table, 'full')) {
      return;
    }
    try {
      if (await this.api.confirm('Verwijderen', `Weet je zeker dat je ${this.recordBeschrijving} wilt verwijderen?`)) {
        this.action = 'delete'; //bezig
        if (!this.isNieuw) {
          //ook echt weghalen
          await this.api.delete(this.table, this.editId);
        }
        //gelukt
        this.afterDelete();
      }
    } catch (e) {
      this.log.error(e);
    }
    this.action = null;
  }

  /**
   * Hook close-actie: moet er eerst bevestiging komen?
   */
  async canClose(): Promise<boolean> {
    if (this.form && this.form.dirty) {
      return await this.api.confirm('Niet opgeslagen', `Er zijn niet opgeslagen wijzigingen. Weet je zeker dat je dit scherm wilt sluiten zonder op te slaan?`);
    }
    return true; //anders alles ok
  }

  ////////////////////// TAAL - overschrijfbaar //////////////////

  /**
   * De beschrijving van dit record. Bijvoorbeeld "de gebruiker 'Sjaak'", of "deze vrachtwagen".
   */
  protected get recordBeschrijving(): string {
    return 'dit record';
  }

  /////////////////////////// Helpers voor schermen //////////////////////////////////
  /**
   * Set het formulier op dirty
   */
  protected setDirty(): void {
    if (this.form) {
      this.log.debug(`setDirty`);
      this.form.form.markAsDirty();
    }
  }


  ////////////////////////////// HOOKS VOOR SUBCLASSES ////////////////////////////////////

  /**
   * Getter voor de defaultoptions bij het ophalen van het record
   * Standaard worden de defaultOptions gebruikt, maar zo kan het dynamischer
   */
  protected get recordOptions(): hash<any> {
    return this.defaultOptions;
  }

  /**
   * Pas het opgehaalde record eventueel nog even aan
   */
  protected afterHaaldata(): void {
    //standaard: doe niets
  }

  /**
   * Als de lookups opnieuw zijn opgehaald. Bij haaldata worden lookups ook gehaald: dan runt deze hook voor afterHaaldata
   * @protected
   */
  protected afterHaalLookups(): void {

  }

  /**
   * Voor het saven aanpassen van de data zoals het opgeslagen wordt (bijvoorbeend een _connected erin). Het teruggegeven resultaat wordt als data meegegeven. Standaard gewoon het record as as
   */
  protected savedata(): Datarecord {
    return this.record;
  }

  /**
   * Na succesvol opslaan. Wordt door de standaard savehandler aangeroepen. Standaard sluit dit de dialoog met het bewerkte record als resultaat
   */
  protected afterSave(): void {
    void this.closeWindow(this.record, true); //geen dialoog meer
  }

  /**
   * Na succesvol verwijderen. Standaard sluit dit de dialoog
   */

  protected afterDelete(): void {
    void this.closeWindow();
  }

  /**
   * Hook na het instellen van het record en de lookups (of de conclusie dat dit niet hoeft)
   */
  protected async dataGezet(): Promise<void> {

  }
}
